// Wochenraster: So–Sa + Feiertage, 0–24 h. Mit dem Uhr-Pinsel malen
// (Maus gedrückt halten), Raster 60/15/5/3/2/1 min, Zoom, Tag kopieren,
// Feiertage. Speichern plant die kommenden Stunden neu.

import { useEffect, useMemo, useState } from "react";
import { api, toast, useLoad } from "../../api";
import { Empty, Field, Modal, Tip, useMe } from "../../ui";

const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa", "Feiertag"];
const STEPS = [60, 15, 5, 3, 2, 1];

/** Expand stored change points to one clock id per minute for each day. */
function expand(cells: any[]): (string | null)[][] {
  const out: (string | null)[][] = [];
  for (let d = 0; d < 8; d++) {
    const mine = cells.filter((c) => c.day === d).sort((a, b) => a.minute - b.minute);
    const row: (string | null)[] = new Array(1440).fill(null);
    let cur: string | null = null;
    let i = 0;
    for (let m = 0; m < 1440; m++) {
      while (i < mine.length && mine[i].minute === m) cur = mine[i++].clock_id;
      row[m] = cur;
    }
    out.push(row);
  }
  return out;
}

/** Minutes → change points. */
function compress(row: (string | null)[]): { minute: number; clock_id: string | null }[] {
  const out: { minute: number; clock_id: string | null }[] = [];
  let prev: string | null | undefined = undefined;
  for (let m = 0; m < 1440; m++) {
    if (row[m] !== prev) {
      out.push({ minute: m, clock_id: row[m] });
      prev = row[m];
    }
  }
  return out;
}

export function Grid() {
  const me = useMe();
  const canEdit = ["admin", "programm"].includes(me?.role);
  const [season, setSeason] = useState<string>("");
  const { data, reload } = useLoad<any>(`/api/grid${season ? `?season=${season}` : ""}`, [season]);
  const [newSeason, setNewSeason] = useState<any | null>(null);
  // Stundenende brush: clicking an hour sets "fest", "weich" or back to the default.
  const [modeBrush, setModeBrush] = useState<"" | "fest" | "weich" | "aus">("");
  const hourMode = (day: number, hour: number) => (data?.hour_modes ?? []).find((x: any) => x.day === day && x.hour === hour)?.mode as string | undefined;
  const setHourMode = async (day: number, hour: number) => {
    try {
      await api("/api/grid/hour-mode", { method: "PUT", body: { day, hour, mode: modeBrush === "aus" ? null : modeBrush, season: season || null } });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const { data: clocks } = useLoad<any[]>("/api/clocks");
  const [rows, setRows] = useState<(string | null)[][]>([]);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [brush, setBrush] = useState<string | null>(null);
  const [step, setStep] = useState(60);
  const [zoom, setZoom] = useState(2);
  const [painting, setPainting] = useState(false);
  const [copyFrom, setCopyFrom] = useState<number | null>(null);
  const [holidays, setHolidays] = useState(false);
  const [full, setFull] = useState(false);

  useEffect(() => {
    if (data) {
      setRows(expand(data.cells));
      setDirty(new Set());
    }
  }, [data]);
  useEffect(() => {
    if (!brush && clocks?.length) setBrush(clocks[0].id);
  }, [clocks]);
  useEffect(() => {
    const up = () => setPainting(false);
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const clockById = useMemo(() => new Map((clocks ?? []).map((c) => [c.id, c])), [clocks]);
  const paint = (day: number, minute: number) => {
    if (!canEdit) return;
    setRows((cur) => {
      const next = cur.map((r) => r.slice());
      for (let m = minute; m < minute + step && m < 1440; m++) next[day][m] = brush;
      return next;
    });
    setDirty((d) => new Set(d).add(day));
  };
  const save = async () => {
    try {
      for (const d of dirty) await api("/api/grid/day", { method: "PUT", body: { day: d, cells: compress(rows[d]), regenerate: false, season: season || null } });
      // One regeneration for all changed days.
      await api("/api/log/generate", { body: { hours: 168, replace: true } });
      toast("Wochenraster gespeichert, die kommende Woche ist neu geplant");
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const copyDay = async (from: number, to: number[]) => {
    await api("/api/grid/copy", { body: { from, to, season: season || null } });
    toast("Tag kopiert");
    setCopyFrom(null);
    reload();
  };

  if (!data || !rows.length) return <div className="card">Lade …</div>;
  const cellH = Math.max(4, Math.round((step / 60) * 16 * zoom));
  const slots = Array.from({ length: 1440 / step }, (_, i) => i * step);
  const w = data.warnings ?? {};
  return (
    <div className="stack" style={full ? { position: "fixed", inset: 0, zIndex: 40, background: "var(--paper)", padding: 16, overflow: "auto" } : undefined}>
      <div className="row wrap" style={{ gap: 6 }}>
        <span className="small muted">Raster:</span>
        <button className="btn small" onClick={() => setSeason("")} style={{ boxShadow: !season ? "inset 0 -3px 0 var(--accent)" : undefined }}>Standard</button>
        {(data?.seasons ?? []).map((x: any) => (
          <button key={x.id} className="btn small" onClick={() => setSeason(x.id)} style={{ boxShadow: season === x.id ? `inset 0 -3px 0 ${x.color}` : undefined, opacity: x.enabled ? 1 : 0.5 }}>
            <span style={{ color: x.color }}>■</span> {x.name} <span className="muted">{x.from_md.split("-").reverse().join(".")}.–{x.to_md.split("-").reverse().join(".")}.</span>
          </button>
        ))}
        {canEdit && <button className="btn small" onClick={() => setNewSeason({ name: "", from: "01.12.", to: "26.12." })}>+ Saison</button>}
        {season && canEdit && <button className="btn small danger" onClick={async () => { if (window.confirm("Saison und ihr Raster löschen?")) { await api(`/api/seasons/${season}`, { method: "DELETE" }); setSeason(""); } }}>Saison löschen</button>}
      </div>
      {season && <Tip>Saison-Raster: gilt jedes Jahr im angegebenen Zeitraum. Tage ohne Eintrag in der Saison nehmen das Standard-Raster.</Tip>}
      {newSeason && (
        <Modal title="Neue Saison" onClose={() => setNewSeason(null)}>
          <div className="form">
            <Field label="Name"><input type="text" placeholder="z. B. Weihnachten" value={newSeason.name} onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })} /></Field>
            <Field label="von (TT.MM.)"><input type="text" value={newSeason.from} onChange={(e) => setNewSeason({ ...newSeason, from: e.target.value })} /></Field>
            <Field label="bis (TT.MM.)" hint="darf über den Jahreswechsel gehen, z. B. 27.12.–06.01."><input type="text" value={newSeason.to} onChange={(e) => setNewSeason({ ...newSeason, to: e.target.value })} /></Field>
          </div>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" />
            <button className="btn primary" onClick={async () => {
              const md = (v: string) => { const m = /^(\d{1,2})\.(\d{1,2})\.?$/.exec(v.trim()); return m ? `${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : ""; };
              try { const r = await api("/api/seasons", { body: { name: newSeason.name, from_md: md(newSeason.from), to_md: md(newSeason.to) } }); setNewSeason(null); setSeason(r.id); } catch (e: any) { toast(e.message, true); }
            }}>Anlegen</button>
          </div>
        </Modal>
      )}
      <div className="row wrap">
        <span className="small muted">Pinsel:</span>
        {(clocks ?? []).map((c) => (
          <button key={c.id} className="btn small" onClick={() => setBrush(c.id)} style={{ borderColor: brush === c.id ? c.color : undefined, boxShadow: brush === c.id ? `inset 0 -3px 0 ${c.color}` : undefined }}>
            <span style={{ color: c.color }}>■</span> {c.name}
          </button>
        ))}
        <button className="btn small" onClick={() => setBrush(null)} style={{ boxShadow: brush === null ? "inset 0 -3px 0 var(--ink-3)" : undefined }}>Radierer</button>
        <span className="small muted" style={{ marginLeft: 10 }}>Stundenende:</span>
        {([["fest", "fest (harter Start zur vollen Stunde)"], ["weich", "weich (Titel spielt aus)"], ["aus", "wie die Uhr"]] as const).map(([k, l]) => (
          <button key={k} className="btn small" title={l} onClick={() => setModeBrush(modeBrush === k ? "" : k)} style={{ boxShadow: modeBrush === k ? "inset 0 -3px 0 var(--accent)" : undefined }}>{k === "aus" ? "zurücksetzen" : k}</button>
        ))}
        {modeBrush && <span className="small" style={{ color: "var(--accent)" }}>Klick auf eine Stunde setzt „{modeBrush === "aus" ? "wie die Uhr" : modeBrush}“</span>}
        <span className="spacer" />
        <select style={{ width: 110 }} value={step} onChange={(e) => setStep(Number(e.target.value))}>{STEPS.map((s) => <option key={s} value={s}>Raster {s} min</option>)}</select>
        <select style={{ width: 90 }} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}>{[1, 2, 3, 4, 6, 8].map((z) => <option key={z} value={z}>Zoom {z}×</option>)}</select>
        <button className="btn small" onClick={() => setHolidays(true)}>Feiertage ({data.holidays.length})</button>
        <button className="btn small" onClick={() => setFull(!full)}>{full ? "Vollbild verlassen" : "Vollbild"}</button>
        {canEdit && <button className="btn primary" disabled={!dirty.size} onClick={save}>{dirty.size ? `Speichern (${dirty.size} Tage)` : "Alle Änderungen gespeichert"}</button>}
      </div>
      {!clocks?.length && <Empty>Zuerst eine Uhr anlegen (Programm › Uhren).</Empty>}
      {(w.days_without_start?.length > 0 || w.empty_clocks?.length > 0) && (
        <div className="banner warn">
          ⚠ {w.days_without_start?.length ? `Ohne Uhr ab 0:00: ${w.days_without_start.join(", ")}. ` : ""}
          {w.empty_clocks?.length ? `Uhren ohne Elemente: ${w.empty_clocks.map((c: any) => c.name).join(", ")}.` : ""}
        </div>
      )}
      <Tip>Pinsel wählen und mit gedrückter Maus über die Stunden ziehen. Feiertage nutzen die Spalte „Feiertag“, sonst den Wochentag. Gesendete und gesperrte Einträge im Sendeplan bleiben beim Neuplanen erhalten.</Tip>
      <div className="card" style={{ padding: 8, overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `48px repeat(8, minmax(90px, 1fr))`, gap: 4, userSelect: "none" }}>
          <div />
          {DAYS.map((d, i) => (
            <div key={d} className="row small" style={{ fontWeight: 600, justifyContent: "center" }}>
              {d}
              {canEdit && <button className="iconbtn" title="Diesen Tag kopieren" onClick={() => setCopyFrom(i)}>⧉</button>}
            </div>
          ))}
          <div>
            {slots.map((m) => <div key={m} className="small muted" style={{ height: cellH, lineHeight: `${cellH}px`, textAlign: "right", paddingRight: 4, visibility: m % 60 === 0 && cellH >= 10 || m % 60 === 0 && step === 60 ? "visible" : "hidden" }}>{String(m / 60).padStart(2, "0")}:00</div>)}
          </div>
          {DAYS.map((_, day) => (
            <div key={day} onMouseLeave={() => undefined}>
              {slots.map((m) => {
                const id = rows[day][m];
                const c = id ? clockById.get(id) : null;
                const first = m === 0 || rows[day][m - step] !== id;
                return (
                  <div key={m}
                    onMouseDown={() => { if (modeBrush) { if (canEdit) void setHourMode(day, Math.floor(m / 60)); return; } setPainting(true); paint(day, m); }}
                    onMouseEnter={() => !modeBrush && painting && paint(day, m)}
                    title={`${DAYS[day]} ${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")} – ${c?.name ?? "keine Uhr"}`}
                    style={{ height: cellH, background: c ? c.color : "#f1eee6", opacity: c ? 0.85 : 1, borderTop: m % 60 === 0 ? "1px solid rgba(0,0,0,0.12)" : undefined, color: "#fff", fontSize: 10, paddingLeft: 3, overflow: "hidden", whiteSpace: "nowrap", cursor: canEdit ? "crosshair" : "default" }}>
                    {m % 60 === 0 && hourMode(day, m / 60) && <span style={{ float: "right", background: hourMode(day, m / 60) === "fest" ? "#c8382f" : "#2f6fb1", color: "#fff", borderRadius: 3, padding: "0 3px", marginRight: 2, fontSize: 9 }} title={`Stundenende ${hourMode(day, m / 60)}`}>{hourMode(day, m / 60) === "fest" ? "F" : "W"}</span>}
                    {first && c && cellH >= 10 ? c.name : ""}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {copyFrom !== null && <CopyDialog from={copyFrom} onCopy={copyDay} onClose={() => setCopyFrom(null)} />}
      {holidays && <Holidays list={data.holidays} canEdit={canEdit} onClose={() => { setHolidays(false); reload(); }} />}
    </div>
  );
}

function CopyDialog({ from, onCopy, onClose }: { from: number; onCopy: (from: number, to: number[]) => void; onClose: () => void }) {
  const [to, setTo] = useState<number[]>([]);
  return (
    <Modal title={`${DAYS[from]} kopieren nach …`} onClose={onClose}>
      <div className="row wrap">
        {DAYS.map((d, i) => i !== from && (
          <label key={d} className="badge" style={{ cursor: "pointer", background: to.includes(i) ? "var(--accent-2)" : undefined }}>
            <input type="checkbox" checked={to.includes(i)} onChange={() => setTo(to.includes(i) ? to.filter((x) => x !== i) : [...to, i])} /> {d}
          </label>
        ))}
      </div>
      <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" disabled={!to.length} onClick={() => onCopy(from, to)}>Kopieren</button></div>
    </Modal>
  );
}

function Holidays({ list, canEdit, onClose }: { list: any[]; canEdit: boolean; onClose: () => void }) {
  const [items, setItems] = useState(list);
  const [v, setV] = useState<any>({ name: "", month: "", day: "", year: "" });
  const add = async () => {
    try {
      const r = await api("/api/holidays", { body: v });
      setItems([...items, r]);
      setV({ name: "", month: "", day: "", year: "" });
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <Modal title="Feiertage" onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }}>An Feiertagen gilt die Spalte „Feiertag“ im Wochenraster; ist sie leer, der normale Wochentag. Jahr leer = jedes Jahr.</p>
      {items.map((h) => (
        <div key={h.id} className="row">
          <b>{h.name}</b><span className="muted">{h.day}.{h.month}.{h.year ?? " (jährlich)"}</span><span className="spacer" />
          {canEdit && <button className="btn small danger" onClick={async () => { await api(`/api/holidays/${h.id}`, { method: "DELETE" }); setItems(items.filter((x) => x.id !== h.id)); }}>✕</button>}
        </div>
      ))}
      {canEdit && (
        <div className="form" style={{ marginTop: 12 }}>
          <Field label="Name"><input type="text" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
          <Field label="Tag / Monat / Jahr">
            <div className="row">
              <input type="number" min={1} max={31} placeholder="TT" value={v.day} onChange={(e) => setV({ ...v, day: e.target.value })} />
              <input type="number" min={1} max={12} placeholder="MM" value={v.month} onChange={(e) => setV({ ...v, month: e.target.value })} />
              <input type="number" placeholder="Jahr" value={v.year} onChange={(e) => setV({ ...v, year: e.target.value })} />
            </div>
          </Field>
          <div className="row full"><span className="spacer" /><button className="btn primary" onClick={add}>Hinzufügen</button></div>
        </div>
      )}
    </Modal>
  );
}
